import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { money, formatApiErrorDetail, formatDateRange } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Users, Plus, Pencil, LogOut, Search, DoorOpen, Building2, Paperclip } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";

const statusTone = { Active: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  Vacated: "bg-muted text-muted-foreground", Past: "bg-muted text-muted-foreground" };

export default function TenantsView() {
  const { user, canWrite } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [leases, setLeases] = useState([]);
  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    const [t, p, u, l, d] = await Promise.all([
      api.get("/tenants"), api.get("/properties"), api.get("/units"), api.get("/leases"), api.get("/documents"),
    ]);
    setTenants(t.data); setProperties(p.data); setUnits(u.data); setLeases(l.data); setDocs(d.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const leaseForTenant = (t) => leases.find((l) => l.tenant_id === t.id && l.status === "Active")
    || leases.find((l) => l.tenant_id === t.id);

  const docCountForTenant = (t) => {
    const leaseIds = new Set(leases.filter((l) => l.tenant_id === t.id).map((l) => l.id));
    return docs.filter((d) => d.tenant_id === t.id || (d.linked_collection === "leases" && leaseIds.has(d.linked_id))).length;
  };

  const openAdd = () => setModal({ editing: null, form: { status: "Active" } });
  const openEdit = (t) => {
    const l = leaseForTenant(t);
    setModal({ editing: t, form: {
      tenant_id: t.id, name: t.name, phone: t.phone, email: t.email,
      property_name: t.property_name, property_id: t.property_id, unit: t.unit,
      monthly_rent: l?.monthly_rent || "", security_deposit: l?.security_deposit || "",
      lease_start: l?.start_date || t.move_in || "", lease_end: l?.end_date || "",
      notes: t.notes || "", lease_id: l?.id || null,
    } });
  };

  const moveOut = async (t) => {
    const l = leaseForTenant(t);
    if (!l) { toast.error("No active lease found for this tenant"); return; }
    if (!window.confirm(`End ${t.name}'s lease and mark their unit available?`)) return;
    try { await api.post(`/leases/${l.id}/end`); toast.success(`${t.name} moved out — unit is now available`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = tenants.filter((t) => !search || JSON.stringify(t).toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="page-tenants">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><Users className="w-7 h-7 text-primary" /> Tenants</h1>
          <p className="text-sm text-muted-foreground mt-1">Assign a tenant to a unit and the lease + unit status update automatically.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-48 rounded-xl" data-testid="search-tenants" />
          </div>
          {canWrite && <Button className="rounded-xl" onClick={openAdd} data-testid="add-tenant-btn"><Plus className="w-4 h-4 mr-1" /> Add Tenant</Button>}
        </div>
      </div>

      <div className="border border-border bg-card rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Tenant</th>
              <th className="px-4 py-3 text-left font-semibold">Property / Unit</th>
              <th className="px-4 py-3 text-left font-semibold">Contact</th>
              <th className="px-4 py-3 text-right font-semibold">Rent</th>
              <th className="px-4 py-3 text-left font-semibold">Lease</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
              <th className="px-4 py-3 text-left font-semibold">Docs</th>
              <th className="px-4 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No tenants yet.</td></tr>}
            {filtered.map((t) => {
              const l = leaseForTenant(t);
              return (
                <tr key={t.id} className="border-t border-border hover:bg-muted/40" data-testid={`tenant-row-${t.id}`}>
                  <td className="px-4 py-3 font-medium">{t.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{t.property_name} {t.unit ? `· ${t.unit}` : ""}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{t.email}<br />{t.phone}</td>
                  <td className="px-4 py-3 text-right font-mono">{money(l?.monthly_rent || 0)}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{l ? formatDateRange(l.start_date, l.end_date) : "—"}</td>
                  <td className="px-4 py-3"><Badge className={`rounded-sm font-normal ${statusTone[t.status] || "bg-muted"}`}>{t.status || "—"}</Badge></td>
                  <td className="px-4 py-3">
                    <button onClick={() => navigate(`/documents?tenant_id=${t.id}&tenant_name=${encodeURIComponent(t.name)}`)}
                      data-testid={`tenant-docs-${t.id}`}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors" title="View attached documents">
                      <Paperclip className="w-3.5 h-3.5" /> {docCountForTenant(t)}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canWrite && (
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(t)} data-testid={`edit-tenant-${t.id}`} className="text-muted-foreground hover:text-primary p-1"><Pencil className="w-4 h-4" /></button>
                        {t.status === "Active" && <button onClick={() => moveOut(t)} data-testid={`moveout-tenant-${t.id}`} className="text-xs text-red-600 hover:underline flex items-center gap-1"><LogOut className="w-3.5 h-3.5" /> Move Out</button>}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <TenantDialog modal={modal} setModal={setModal} properties={properties} units={units}
          onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

function TenantDialog({ modal, setModal, properties, units, onSaved }) {
  const [form, setForm] = useState(modal.form || {});
  const [saving, setSaving] = useState(false);
  const [addingUnit, setAddingUnit] = useState(false);
  const propertyUnits = units.filter((u) => u.property_id === form.property_id);

  const setProperty = (name) => {
    const p = properties.find((x) => x.name === name);
    setForm((prev) => ({ ...prev, property_name: name, property_id: p?.id || "", unit: "" }));
    setAddingUnit(false);
  };
  const setUnit = (value) => {
    const u = propertyUnits.find((x) => x.unit_number === value);
    setForm((prev) => ({ ...prev, unit: value, monthly_rent: prev.monthly_rent || u?.rent_amount || "" }));
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Tenant name is required"); return; }
    if (!form.property_id) { toast.error("Select a property"); return; }
    if (!form.unit?.trim()) { toast.error("Select or enter a unit"); return; }
    setSaving(true);
    try {
      await api.post("/tenants/assign", {
        tenant_id: form.tenant_id || null, name: form.name, phone: form.phone || "", email: form.email || "",
        property_id: form.property_id, property_name: form.property_name, unit: form.unit,
        monthly_rent: Number(form.monthly_rent) || 0, security_deposit: Number(form.security_deposit) || 0,
        lease_start: form.lease_start || "", lease_end: form.lease_end || "", notes: form.notes || "",
        lease_id: form.lease_id || null,
        can_email: form.can_email || "Yes", can_sms: form.can_sms || "Yes",
        preferred_method: form.preferred_method || "Email", opted_out: form.opted_out || "No",
      });
      toast.success("Tenant saved — lease & unit updated automatically");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const field = (label, key, type = "text") => (
    <div>
      <label className="text-xs font-medium text-muted-foreground mb-1 block">{label}</label>
      <Input data-testid={`tenant-${key}`} type={type} value={form[key] ?? ""} onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))} className="rounded-xl" />
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && setModal(null)}>
      <DialogContent className="rounded-xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tenant-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{modal.editing ? "Edit" : "Add"} Tenant</DialogTitle>
          <DialogDescription>Assigning a unit auto-creates/links the lease and marks the unit Occupied.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          {field("Tenant Name", "name")}
          {field("Phone", "phone")}
          {field("Email", "email", "email")}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Property</label>
            <Select value={form.property_name || ""} onValueChange={setProperty}>
              <SelectTrigger className="rounded-xl" data-testid="tenant-property"><SelectValue placeholder="Select property..." /></SelectTrigger>
              <SelectContent>{properties.map((p) => <SelectItem key={p.id} value={p.name}><Building2 className="w-3 h-3 inline mr-1" />{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 flex items-center justify-between">
              <span>Unit</span>
              {form.property_id && <button type="button" onClick={() => { setAddingUnit(!addingUnit); setForm((p) => ({ ...p, unit: "" })); }} className="text-[11px] text-primary hover:underline" data-testid="tenant-toggle-add-unit">{addingUnit ? "Pick existing" : "+ Add new unit"}</button>}
            </label>
            {addingUnit ? (
              <Input data-testid="tenant-unit-new" placeholder="e.g. Unit 5" value={form.unit ?? ""} onChange={(e) => setForm((p) => ({ ...p, unit: e.target.value }))} className="rounded-xl" />
            ) : (
              <Select value={form.unit || ""} onValueChange={setUnit} disabled={!form.property_id}>
                <SelectTrigger className="rounded-xl" data-testid="tenant-unit"><SelectValue placeholder={form.property_id ? "Select unit..." : "Select property first"} /></SelectTrigger>
                <SelectContent>{propertyUnits.map((u) => <SelectItem key={u.id} value={u.unit_number}><DoorOpen className="w-3 h-3 inline mr-1" />{u.unit_number}{u.status === "Occupied" ? " · occupied" : " · available"}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          {field("Monthly Rent", "monthly_rent", "number")}
          {field("Security Deposit", "security_deposit", "number")}
          {field("Lease Start", "lease_start", "date")}
          {field("Lease End", "lease_end", "date")}
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Notes</label>
            <Textarea data-testid="tenant-notes" value={form.notes || ""} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} className="rounded-xl" />
          </div>
          <div className="sm:col-span-2 pt-2 mt-1 border-t border-border">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">Communication Preferences</p>
          </div>
          {[
            ["Can Receive Email", "can_email", ["Yes", "No"]],
            ["Can Receive SMS", "can_sms", ["Yes", "No"]],
            ["Preferred Method", "preferred_method", ["Email", "SMS", "Both"]],
            ["Opted Out of SMS", "opted_out", ["No", "Yes"]],
          ].map(([label, key, opts]) => (
            <div key={key}>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">{label}</label>
              <Select value={form[key] || opts[0]} onValueChange={(v) => setForm((p) => ({ ...p, [key]: v }))}>
                <SelectTrigger className="rounded-xl" data-testid={`tenant-${key}`}><SelectValue /></SelectTrigger>
                <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => setModal(null)}>Cancel</Button>
          <Button className="rounded-xl" onClick={save} disabled={saving} data-testid="tenant-save">{saving ? "Saving..." : "Save Tenant"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
